import React, { useState } from 'react';
import { Box, Grid, Select, Spinner } from '@chakra-ui/react';
import { Text } from '~/common/components/defaultUIComponents';
import ProjectRecordApi from '~/common/api/zeiterfassungServerApi/ProjectRecordApi';
import { useDoneTotals } from '~/common/api/orderingServiceApi/OrderingApi';
import { ProjectHoursByEmployeeChart } from './ProjectHoursByEmployeeChart';
import { ProjectEmployeeFactorChart } from './ProjectEmployeeFactorChart';
import { FactorTrendChart } from './FactorTrendChart';
import { TimeComparisonChart } from './TimeComparisonChart';
import { ProjectRecordsCollapsible } from './ProjectRecordsCollapsible';
import useTranslate from '~/common/utils/translate';

interface Props {
  start: string;
  end: string;
  projects: { id: number; name: string }[];
}

export const ProjectBreakdown: React.FC<Props> = ({ start, end, projects }) => {
  const t = useTranslate();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null,
  );

  const { data: breakdown, isLoading: breakdownLoading } =
    ProjectRecordApi.hooks.useProjectBreakdown(start, end, selectedProjectId);

  const { data: doneTotals } = useDoneTotals(start, end, selectedProjectId);

  return (
    <Box>
      <Text fontSize="md" fontWeight="600" mb={3}>
        {t('projectRecords.projectBreakdown')}
      </Text>
      <Select
        placeholder={t('projectRecords.selectProject')}
        maxW="300px"
        mb={4}
        value={selectedProjectId ?? ''}
        onChange={(e) =>
          setSelectedProjectId(e.target.value ? Number(e.target.value) : null)
        }
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </Select>

      {selectedProjectId == null && (
        <Text fontSize="sm" color="gray.500">
          {t('projectRecords.selectProjectHint')}
        </Text>
      )}

      {selectedProjectId != null && breakdownLoading && <Spinner />}

      {selectedProjectId != null && breakdown && (
        <>
          {breakdown.by_employee.length > 0 && (
            <>
              <ProjectHoursByEmployeeChart data={breakdown.by_employee} />
              <Box mt={3}>
                <ProjectEmployeeFactorChart data={breakdown.by_employee} />
              </Box>
            </>
          )}
          <Grid templateColumns="1fr 1fr" gap={3} mt={3}>
            {breakdown.factor_trend.length > 0 && (
              <FactorTrendChart data={breakdown.factor_trend} />
            )}
            <TimeComparisonChart
              breakdown={breakdown}
              doneTotals={doneTotals}
            />
          </Grid>
          <Box mt={3}>
            <ProjectRecordsCollapsible
              start={start}
              end={end}
              projectId={selectedProjectId}
              recordCount={breakdown.record_count}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
